// Invite another designer — referrer earns +0.5% on their showroom sales for 6 months after first delivered sale.
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { createDesignerInvite, listDesignerInviteOutbox, inviteBonusProgress } from "../../lib/db";
import { Btn, Field, Input, Toast, Spinner, ProgressBar, SectionLabel } from "../../components/UI";

export default function DesignerInviteProgram() {
  const { profile, role } = useAuth();
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ msg: "", type: "success" });

  const load = () => {
    if (!profile?.uid || role !== "designer") return;
    listDesignerInviteOutbox(profile.uid).then((r) => {
      setRows(r);
      setLoading(false);
    });
  };

  useEffect(() => {
    if (!profile?.uid) return;
    if (role !== "designer") {
      setLoading(false);
      return;
    }
    load();
  }, [profile?.uid, role]);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const inviteLink = (token) => `${baseUrl}/register?invite=${token}`;

  const handleCreate = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) {
      setToast({ msg: "Enter a valid email for your invitee.", type: "error" });
      return;
    }
    setBusy(true);
    try {
      const token = await createDesignerInvite(profile.uid, profile.name || "Designer", em);
      setEmail("");
      setToast({ msg: `Invitation created. Share the link below with ${em}.`, type: "success" });
      await navigator.clipboard?.writeText(inviteLink(token));
      load();
    } catch (err) {
      setToast({ msg: err.message || "Could not create invite.", type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return <Spinner />;
  if (role !== "designer") {
    return (
      <div style={{ padding: "36px 48px" }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300 }}>Designer invites</h1>
        <p style={{ fontSize: 14, color: "var(--mid)", marginTop: 12 }}>
          Program invites are available to designers only. Contractors can still use <strong>Submit client referral</strong> for client leads.
        </p>
      </div>
    );
  }

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={() => setToast({ msg: "" })} />
      <div style={{ padding: "36px 48px 0" }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300 }}>Invite a designer</h1>
        <p style={{ fontSize: 13, color: "var(--mid)", marginTop: 4 }}>
          Send a private signup link. After they join and their first order is marked <strong>Delivered</strong>, you earn an extra{" "}
          <strong>0.5%</strong> on their referred sales for six months — unlimited invites.
        </p>
      </div>

      <div style={{ padding: "24px 48px 48px", maxWidth: 720 }}>
        <div style={{ background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: 4, padding: 28, marginBottom: 28 }}>
          <SectionLabel>New invitation</SectionLabel>
          <form onSubmit={handleCreate}>
            <Field label="Invitee email" hint="They must register with this exact email address.">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="designer@studio.com" disabled={busy} />
            </Field>
            <Btn type="submit" variant="gold" disabled={busy}>
              {busy ? "Creating…" : "Create invite link"}
            </Btn>
          </form>
          <p style={{ fontSize: 11, color: "var(--mid)", marginTop: 14 }}>
            After creating, the link is copied to your clipboard when your browser allows it.
          </p>
        </div>

        <SectionLabel>Your invitations</SectionLabel>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--mid)" }}>No invitations yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {rows.map((inv) => {
              const prog = inviteBonusProgress(inv);
              const link = inviteLink(inv.token);
              return (
                <div key={inv.token} style={{ border: "1px solid var(--border)", borderRadius: 4, padding: 18, background: "#fff" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>{inv.inviteeEmail}</div>
                  <div style={{ fontSize: 11, color: "var(--mid)", wordBreak: "break-all", marginBottom: 10 }}>
                    {link}{" "}
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ color: "var(--gold)", cursor: "pointer", marginLeft: 8 }}
                      onClick={() => navigator.clipboard?.writeText(link)}
                      onKeyDown={(e) => e.key === "Enter" && navigator.clipboard?.writeText(link)}
                    >
                      Copy
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--mid)", marginBottom: 8 }}>{prog.label}</div>
                  {prog.phase === "active" && (
                    <>
                      <ProgressBar value={prog.pct} max={100} label="Bonus window progress" />
                      {inv.bonusEndsAt && (
                        <div style={{ fontSize: 10, color: "var(--light)", marginTop: 6 }}>
                          Ends {inv.bonusEndsAt.toDate?.().toLocaleDateString?.() ?? "—"}
                        </div>
                      )}
                    </>
                  )}
                  {typeof inv.referrerBonusEarnedTotal === "number" && inv.referrerBonusEarnedTotal > 0 && (
                    <div style={{ fontSize: 11, color: "var(--gold)", marginTop: 8 }}>
                      Bonus earned so far: ${Number(inv.referrerBonusEarnedTotal).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
